// The pocket radio: what's on air, carried with you.
//
// Solo play on a 16.4 km planet is otherwise silent, and a DJ playing to
// three people in a room is playing to three people. This lets the set
// reach anyone out on the ground without diluting what the Artist ladder
// measures — listening on the radio is deliberately NOT attendance. Being
// in the room still means being in the room.

import { streamPathForClub } from "@bebop/shared";
import { escapeHtml } from "./escape";

interface LiveClub {
  clubId: string;
  label: string;
  nowPlaying: string;
}

/** Re-check the dial on this cadence while the radio UI is on screen. */
const POLL_MS = 15_000;

export class PocketRadio {
  private root: HTMLDivElement;
  private list: HTMLDivElement;
  private tunedTo: string | undefined;
  private timer: number | undefined;
  private visible = false;

  constructor(
    parent: HTMLElement,
    private audio: {
      setStream(url: string, position: { x: number; y: number; z: number }): void;
      setRadio(on: boolean): void;
      setLive(live: boolean): void;
      unlock(): void;
    },
  ) {
    this.root = document.createElement("div");
    this.root.className = "pocket-radio";
    this.root.innerHTML = `<div class="radio-dial"></div>`;
    this.root.style.display = "none";
    parent.append(this.root);
    this.list = this.root.querySelector(".radio-dial") as HTMLDivElement;
  }

  /** Only meaningful outdoors; indoors you can simply hear the room. */
  setVisible(visible: boolean): void {
    if (visible === this.visible) return;
    this.visible = visible;
    this.root.style.display = visible ? "" : "none";
    if (visible) {
      void this.refresh();
      this.timer = window.setInterval(() => void this.refresh(), POLL_MS);
    } else {
      if (this.timer !== undefined) window.clearInterval(this.timer);
      this.timer = undefined;
      this.tuneOut();
    }
  }

  private async refresh(): Promise<void> {
    try {
      const res = await fetch("/api/live");
      if (!res.ok) throw new Error(String(res.status));
      const { live } = (await res.json()) as { live: LiveClub[] };
      this.render(live);
    } catch {
      this.list.innerHTML = "";
    }
  }

  private render(live: LiveClub[]): void {
    // Whatever we were tuned to went off air: fall silent honestly.
    if (this.tunedTo && !live.some((c) => c.clubId === this.tunedTo)) this.tuneOut();

    if (live.length === 0) {
      this.list.innerHTML = `<span class="radio-idle">— nothing on air —</span>`;
      return;
    }
    this.list.innerHTML = "";
    for (const club of live) {
      const button = document.createElement("button");
      const on = this.tunedTo === club.clubId;
      button.className = `radio-station${on ? " on" : ""}`;
      button.innerHTML = `<span class="radio-dot"></span>${escapeHtml(club.label)}${
        club.nowPlaying ? ` <em>${escapeHtml(club.nowPlaying)}</em>` : ""
      }`;
      button.addEventListener("click", () => {
        if (this.tunedTo === club.clubId) this.tuneOut();
        else this.tuneTo(club.clubId);
        this.render(live);
      });
      this.list.append(button);
    }
  }

  private tuneTo(clubId: string): void {
    this.audio.unlock();
    this.audio.setRadio(true);
    this.audio.setStream(streamPathForClub(clubId), { x: 0, y: 0, z: 0 });
    this.audio.setLive(true);
    this.tunedTo = clubId;
  }

  private tuneOut(): void {
    if (!this.tunedTo) return;
    this.audio.setLive(false);
    this.audio.setRadio(false);
    this.tunedTo = undefined;
  }

  /** Are we listening on the radio right now? (the frame loop asks) */
  get tuned(): boolean {
    return this.tunedTo !== undefined;
  }
}
